import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { speakCall, buildCallText, primeAudio, audioAlreadyAllowed, speechSupported, playChime, playVoiceUrl } from "@/lib/speech";
import { MediaPanel } from "@/components/tv/TvMedia";
import { BrandIcon } from "@/components/Brand";
import {
  Volume2, VolumeX, Clock, ChevronRight, History, Maximize, Minus, Plus, Frame, SkipForward,
} from "lucide-react";

const FILL = { position: "absolute", top: 0, left: 0, width: "100%", height: "100%" };

function firstLast(name) {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  if (parts.length <= 2) return name;
  return `${parts[0]} ${parts[parts.length - 1]}`;
}

function groupByRoom(items) {
  const hasRooms = items.some((p) => p.target_room_name);
  if (!hasRooms) return [{ key: "all", label: null, items }];
  const map = new Map();
  for (const p of items) {
    const key = p.target_room_id || "geral";
    const label = p.target_room_name || "Fila geral";
    if (!map.has(key)) map.set(key, { key, label, items: [] });
    map.get(key).items.push(p);
  }
  return Array.from(map.values());
}

function normalizeMedia(clinic) {
  const list = (clinic?.media_items || []).map((m) =>
    typeof m === "string" ? { type: "image", url: m } : m
  );
  if (clinic?.video_url) list.push({ type: "embed", url: clinic.video_url });
  return list;
}

function Clockface() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <span className="tabular-nums">
      {now.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
    </span>
  );
}

function useLocalNumber(key, initial) {
  const [val, setVal] = useState(() => {
    const v = window.localStorage.getItem(key);
    return v == null || isNaN(Number(v)) ? initial : Number(v);
  });
  const set = useCallback((nv) => {
    setVal(nv);
    try { window.localStorage.setItem(key, String(nv)); } catch (e) { /* ignore */ }
  }, [key]);
  return [val, set];
}

function useLandscape() {
  const calc = () => window.innerWidth >= window.innerHeight;
  const [land, setLand] = useState(calc);
  useEffect(() => {
    const on = () => setLand(calc());
    window.addEventListener("resize", on);
    window.addEventListener("orientationchange", on);
    return () => {
      window.removeEventListener("resize", on);
      window.removeEventListener("orientationchange", on);
    };
  }, []);
  return land;
}

function useWakeLock(active) {
  useEffect(() => {
    if (!active || !navigator.wakeLock) return;
    let lock = null;
    const req = () => navigator.wakeLock.request("screen").then((l) => { lock = l; }).catch(() => {});
    const onVis = () => { if (document.visibilityState === "visible") req(); };
    req();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      if (lock) lock.release().catch(() => {});
    };
  }, [active]);
}

function useTvSession(clinicId) {
  const [tvBlock, setTvBlock] = useState(null);
  const sessionId = useRef(
    (() => {
      const key = `tv_session_${clinicId}`;
      let sid = sessionStorage.getItem(key);
      if (!sid) {
        sid = (window.crypto && crypto.randomUUID && crypto.randomUUID()) || `${Date.now()}-${Math.random()}`;
        sessionStorage.setItem(key, sid);
      }
      return sid;
    })()
  );
  useEffect(() => {
    let active = true;
    const beat = async () => {
      try {
        const { data } = await axios.post(`${API}/tv/${clinicId}/heartbeat`, { session_id: sessionId.current });
        if (active) setTvBlock(data.allowed ? null : data);
      } catch (e) { /* ignore transient errors */ }
    };
    beat();
    const t = setInterval(beat, 30000);
    const release = () => {
      try {
        navigator.sendBeacon && navigator.sendBeacon(
          `${API}/tv/${clinicId}/release`,
          new Blob([JSON.stringify({ session_id: sessionId.current })], { type: "application/json" })
        );
      } catch (e) { /* ignore */ }
    };
    window.addEventListener("pagehide", release);
    return () => {
      active = false;
      clearInterval(t);
      window.removeEventListener("pagehide", release);
      release();
    };
  }, [clinicId]);
  return tvBlock;
}

function TvBlocked({ tvBlock }) {
  const isLimit = tvBlock.reason === "tv_limit";
  return (
    <div data-testid="tv-blocked" className="min-h-screen w-full flex flex-col items-center justify-center bg-ink text-cream p-10 text-center">
      <BrandIcon className="w-24 h-24 mb-6" />
      <h1 className="font-heading text-4xl font-bold mb-3">
        {isLimit ? "Limite de TVs atingido" : "Assinatura necessária"}
      </h1>
      <p className="text-cream/70 text-xl max-w-xl mx-auto">
        {isLimit
          ? `O seu plano permite ${tvBlock.limit} painel(éis) de TV ativo(s) ao mesmo tempo. Feche outra TV ou faça upgrade do plano para liberar esta tela.`
          : "O acesso a esta clínica está suspenso. Assine um plano para reativar o painel."}
      </p>
    </div>
  );
}

function ArmScreen({ clinic, color, onStart }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.focus(); }, []);
  return (
    <button ref={ref} data-testid="tv-arm-button" onClick={onStart} autoFocus
      className="min-h-screen w-full flex flex-col items-center justify-center bg-ink text-cream cursor-pointer outline-none">
      <BrandIcon className="w-24 h-24 mb-6" />
      <h1 className="font-heading text-4xl font-bold mb-4">{clinic?.name || "O Seu Chamador"}</h1>
      <span className="inline-flex items-center gap-3 bg-gold text-ink font-heading font-bold text-2xl px-10 py-5 rounded-md">
        <Volume2 className="w-7 h-7" /> Ativar som e iniciar painel
      </span>
      <p className="text-cream/50 text-base mt-5">Toque na tela ou aperte OK no controle remoto. A TV lembrará esta escolha.</p>
    </button>
  );
}

function SoundOverlay({ onUnlock }) {
  return (
    <button data-testid="tv-unmute-overlay" onClick={onUnlock}
      className="absolute z-40 flex items-center gap-3 bg-gold text-ink font-heading font-bold rounded-md shadow-2xl animate-pulse"
      style={{ bottom: "3vmin", left: "3vmin", padding: "1.6vmin 3vmin", fontSize: "2.6vmin" }}>
      <VolumeX style={{ width: "3vmin", height: "3vmin" }} /> Toque na tela ou aperte OK para ativar o som
    </button>
  );
}

function TvToolbar({ hasMedia, canSkip, onSkip, volume, setVolume, safe, setSafe, voiceOk, voiceError, onTestVoice }) {
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    let t = setTimeout(() => setVisible(false), 6000);
    const show = () => {
      setVisible(true);
      clearTimeout(t);
      t = setTimeout(() => setVisible(false), 6000);
    };
    window.addEventListener("pointermove", show);
    window.addEventListener("keydown", show);
    return () => {
      clearTimeout(t);
      window.removeEventListener("pointermove", show);
      window.removeEventListener("keydown", show);
    };
  }, []);
  const fullscreen = () => {
    const el = document.documentElement;
    const req = el.requestFullscreen || el.webkitRequestFullscreen;
    if (document.fullscreenElement) document.exitFullscreen();
    else if (req) req.call(el);
  };
  const btn = "text-cream hover:text-gold transition-colors p-1";
  return (
    <div data-testid="tv-toolbar"
      className="absolute z-30 flex items-center gap-3 bg-ink/85 rounded-md px-3 py-2 shadow-lg transition-opacity duration-500"
      style={{ bottom: "2vmin", right: "2vmin", opacity: visible || voiceError ? 1 : 0 }}>
      {!voiceOk && <span className="text-xs text-red-300" data-testid="tv-voice-unsupported">Voz indisponível neste navegador</span>}
      {voiceOk && voiceError && (
        <span className="text-xs text-red-300 max-w-[14rem]" data-testid="tv-voice-error">
          {voiceError === "not-allowed" ? "Voz bloqueada: toque na tela ou aperte OK" : `Falha na voz (${voiceError}). Toque em “Testar voz”.`}
        </span>
      )}
      <button onClick={onTestVoice} data-testid="tv-test-voice"
        className="text-xs font-semibold text-ink bg-gold hover:bg-gold-hover rounded-sm px-2.5 py-1.5 transition-colors">
        Testar voz
      </button>
      {hasMedia && (
        <div className="flex items-center gap-2" data-testid="tv-volume-control">
          <button onClick={() => setVolume(volume > 0 ? 0 : 1)} data-testid="tv-volume-toggle" className={btn} aria-label="Mudo">
            {volume > 0 ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
          </button>
          <input type="range" min="0" max="1" step="0.05" value={volume}
            onChange={(e) => setVolume(Number(e.target.value))}
            data-testid="tv-volume-slider" className="w-24 accent-gold cursor-pointer" />
        </div>
      )}
      {canSkip && (
        <button onClick={onSkip} data-testid="tv-skip-media" title="Próxima mídia (seta → no controle)"
          className="flex items-center gap-1 text-xs font-semibold text-cream hover:text-gold transition-colors border-l border-white/15 pl-3">
          <SkipForward className="w-4 h-4" /> Próxima
        </button>
      )}
      <div className="flex items-center gap-1 border-l border-white/15 pl-3" title="Margem de segurança (TVs que cortam as bordas)">
        <Frame className="w-4 h-4 text-gold" />
        <button className={btn} data-testid="tv-safe-minus" onClick={() => setSafe(Math.max(0, safe - 1))}><Minus className="w-4 h-4" /></button>
        <span className="text-cream text-xs w-20 text-center whitespace-nowrap" data-testid="tv-safe-value">Margem {safe}%</span>
        <button className={btn} data-testid="tv-safe-plus" onClick={() => setSafe(Math.min(8, safe + 1))}><Plus className="w-4 h-4" /></button>
      </div>
      <button className={`${btn} border-l border-white/15 pl-3`} data-testid="tv-fullscreen" onClick={fullscreen} aria-label="Tela cheia">
        <Maximize className="w-5 h-5" />
      </button>
    </div>
  );
}

function CallHero({ call, clinic, color, hasMedia, landscape }) {
  const showName = clinic?.display_mode !== "ticket_room";
  const showRoom = clinic?.display_mode !== "name_only";
  const nameSize = hasMedia && landscape ? "7.5vmin" : "11vmin";
  if (!call) {
    return (
      <div className="text-center animate-fade-up">
        <h1 className="font-heading font-bold text-ink" style={{ fontSize: "5.5vmin", lineHeight: 1.1, marginBottom: "1.5vmin" }}>
          {clinic?.welcome_message || "Seja bem-vindo(a)!"}
        </h1>
        <p className="text-cappuccino" style={{ fontSize: "3vmin" }}>Aguardando a próxima chamada…</p>
      </div>
    );
  }
  return (
    <div key={call.call_version} className="animate-call-pop w-full">
      <p className="font-heading font-bold uppercase" style={{ color, fontSize: "2vmin", letterSpacing: "0.25em", marginBottom: "2vmin" }}>
        Chamando agora
      </p>
      {showName && (
        <h1 data-testid="tv-patient-name"
          className="font-heading font-black tracking-tighter uppercase text-ink break-words"
          style={{ fontSize: nameSize, lineHeight: 0.95 }}>
          {firstLast(call.name)}
        </h1>
      )}
      {call.ticket && clinic?.display_mode === "ticket_room" && (
        <h1 className="font-heading font-black tracking-tighter uppercase text-ink" style={{ fontSize: "13vmin" }}>
          {call.ticket}
        </h1>
      )}
      {showRoom && call.room_name && (
        <div data-testid="tv-room-name"
          className="inline-flex items-center rounded-md font-heading font-bold text-white"
          style={{ background: color, fontSize: "4.5vmin", padding: "1.6vmin 4vmin", marginTop: "3vmin" }}>
          {call.room_name}
        </div>
      )}
    </div>
  );
}

function QueueLists({ upNext, recent, color }) {
  const item = { fontSize: "2.6vmin" };
  const label = { fontSize: "1.6vmin", letterSpacing: "0.2em", marginBottom: "1.2vmin" };
  return (
    <div className="flex shrink-0" style={{ height: "34%", minHeight: 0 }}>
      <div className="bg-white border border-border rounded-md flex flex-col overflow-hidden" style={{ width: "50%", marginRight: "1vmin", padding: "2vmin" }}>
        <p className="font-heading font-bold uppercase flex items-center" style={{ ...label, color }}>
          <ChevronRight className="w-4 h-4 mr-1" /> Próximos
        </p>
        <div className="overflow-hidden" data-testid="tv-up-next">
          {upNext.length === 0 && <p className="text-cappuccino/60" style={item}>Ninguém aguardando.</p>}
          {groupByRoom(upNext).map((grp) => (
            <div key={grp.key} style={{ marginBottom: "1vmin" }}>
              {grp.label && <p className="font-bold uppercase tracking-wider text-cappuccino/70" style={{ fontSize: "1.4vmin" }}>{grp.label}</p>}
              {grp.items.map((p, i) => (
                <div key={p.id} className="flex items-center" style={{ padding: "0.6vmin 0" }}>
                  <span className="font-heading font-bold" style={{ ...item, color, width: "4vmin" }}>{i + 1}</span>
                  <span className="font-heading font-semibold text-ink truncate" style={item}>{firstLast(p.name)}</span>
                  {p.priority && <span className="ml-auto font-bold uppercase bg-gold text-ink px-2 rounded-sm" style={{ fontSize: "1.3vmin" }}>Prioridade</span>}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
      <div className="bg-ink text-cream rounded-md flex flex-col overflow-hidden" style={{ width: "50%", marginLeft: "1vmin", padding: "2vmin" }}>
        <p className="font-heading font-bold uppercase flex items-center text-gold" style={label}>
          <History className="w-4 h-4 mr-1" /> Já chamados
        </p>
        <div className="overflow-hidden" data-testid="tv-recent">
          {recent.length === 0 && <p className="text-cream/40" style={item}>Nenhuma chamada ainda.</p>}
          {recent.map((p) => (
            <div key={p.id} className="flex items-center justify-between border-b border-white/5" style={{ padding: "0.6vmin 0" }}>
              <span className="font-heading font-semibold truncate" style={item}>{firstLast(p.name)}</span>
              <span className="font-bold rounded-sm px-2 shrink-0 ml-2" style={{ background: color, color: "#fff", fontSize: "1.8vmin" }}>{p.room_name}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function TVPanel() {
  const { clinicId } = useParams();
  const autoKey = `tv_autostart_${clinicId}`;
  const [state, setState] = useState(null);
  const [armed, setArmed] = useState(() => window.localStorage.getItem(autoKey) === "1");
  const [soundOn, setSoundOn] = useState(() => window.localStorage.getItem(autoKey) === "1" && audioAlreadyAllowed());
  const [flash, setFlash] = useState(false);
  const [ducking, setDucking] = useState(false);
  const [volume, setVolume] = useLocalNumber(`tv_vol_${clinicId}`, 1);
  const [safe, setSafe] = useLocalNumber(`tv_safe_${clinicId}`, 0);
  const [voiceError, setVoiceError] = useState(null);
  const [skipSignal, setSkipSignal] = useState(0);
  const skipMedia = useCallback(() => setSkipSignal((n) => n + 1), []);
  const settingsRef = useRef({ voice: "female", speed: "normal", lang: "pt-BR" });
  const lastVersion = useRef(0);
  const lastCallRef = useRef(null);
  const missedRef = useRef(false);
  const landscape = useLandscape();
  const tvBlock = useTvSession(clinicId);
  useWakeLock(armed);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "ArrowRight" || e.key === "MediaTrackNext" || e.keyCode === 417) skipMedia(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [skipMedia]);

  const remoteSkip = state?.clinic?.media_skip;
  const lastRemoteSkip = useRef(null);
  useEffect(() => {
    if (remoteSkip == null) return;
    if (lastRemoteSkip.current != null && remoteSkip > lastRemoteSkip.current) skipMedia();
    lastRemoteSkip.current = remoteSkip;
  }, [remoteSkip, skipMedia]);

  const announce = useCallback((text, withVoice, url) => {
    setDucking(true);
    const safety = setTimeout(() => setDucking(false), 25000);
    const done = () => { clearTimeout(safety); setDucking(false); };
    const fallback = () => speakCall(text, {
      rate: { slow: 0.8, normal: 0.92, fast: 1.1 }[settingsRef.current.speed] || 0.92,
      lang: settingsRef.current.lang,
      onStart: () => setVoiceError(null),
      onEnd: done,
      onError: (code) => {
        setVoiceError(code);
        if (code === "not-allowed") { missedRef.current = true; setSoundOn(false); }
      },
    });
    playChime().then(async () => {
      if (!withVoice) return done();
      if (url && await playVoiceUrl(url)) { setVoiceError(null); return done(); }
      fallback();
    });
  }, []);

  const voiceQs = () => `voice=${settingsRef.current.voice}&speed=${settingsRef.current.speed}&lang=${settingsRef.current.lang}`;
  const callUrl = useCallback((c) => `${API}/tv/${clinicId}/call-audio/${c.id}?v=${c.call_version}&${voiceQs()}`, [clinicId]);

  const unlock = useCallback(() => {
    primeAudio();
    setSoundOn(true);
    setArmed(true);
    try { window.localStorage.setItem(autoKey, "1"); } catch (e) { /* ignore */ }
    if (missedRef.current && lastCallRef.current) {
      missedRef.current = false;
      const c = lastCallRef.current;
      setTimeout(() => announce(buildCallText(c.name, c.room_name), true, callUrl(c)), 400);
    }
  }, [autoKey, announce, callUrl]);

  const testVoice = useCallback(() => {
    unlock();
    setTimeout(() => announce("Teste de voz. O chamador está funcionando.", true, `${API}/tv/${clinicId}/test-audio?${voiceQs()}`), 300);
  }, [unlock, announce, clinicId]);

  useEffect(() => {
    if (!armed || soundOn) return;
    const on = () => unlock();
    window.addEventListener("pointerdown", on);
    window.addEventListener("keydown", on);
    window.addEventListener("touchstart", on);
    return () => {
      window.removeEventListener("pointerdown", on);
      window.removeEventListener("keydown", on);
      window.removeEventListener("touchstart", on);
    };
  }, [armed, soundOn, unlock]);

  const fetchState = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/tv/${clinicId}`);
      if (!data || typeof data !== "object" || !data.clinic) return;
      settingsRef.current = { voice: data.clinic?.tts_voice || "female", speed: data.clinic?.tts_speed || "normal", lang: data.clinic?.tts_language || "pt-BR" };
      setState(data);
    } catch (e) { /* keep last state on error */ }
  }, [clinicId]);

  useEffect(() => {
    fetchState();
    const t = setInterval(fetchState, 2500);
    return () => clearInterval(t);
  }, [fetchState]);

  useEffect(() => {
    const call = state?.current_call;
    if (!call || call.call_version <= lastVersion.current) return;
    const first = lastVersion.current === 0;
    lastVersion.current = call.call_version;
    lastCallRef.current = call;
    if (first && !armed) return;
    setFlash(true);
    const tf = setTimeout(() => setFlash(false), 900);
    if (armed && soundOn) {
      announce(buildCallText(call.name, call.room_name), state?.clinic?.speech_enabled !== false, callUrl(call));
    } else if (armed) {
      missedRef.current = true;
    }
    return () => clearTimeout(tf);
  }, [state, armed, soundOn, announce, callUrl]);

  const clinic = state?.clinic;
  const call = state?.current_call;
  const color = clinic?.primary_color || "#C5A059";
  const mediaSig = JSON.stringify(clinic?.media_items || []) + "|" + (clinic?.video_url || "");
  const mediaItems = useMemo(() => normalizeMedia(clinic), [mediaSig]); // eslint-disable-line react-hooks/exhaustive-deps
  const hasMedia = mediaItems.length > 0;
  const activeCalls = state?.active_calls || [];
  const clinicVol = clinic?.media_volume == null ? 0.8 : clinic.media_volume / 100;
  const mediaVol = volume * clinicVol * (ducking ? 0.15 : 1);

  if (tvBlock) return <TvBlocked tvBlock={tvBlock} />;
  if (!armed) return <ArmScreen clinic={clinic} color={color} onStart={unlock} />;

  const callerSize = hasMedia ? (landscape ? { width: "60%", height: "100%" } : { width: "100%", height: "62%" }) : { width: "100%", height: "100%" };
  const mediaSize = landscape ? { width: "40%", height: "100%" } : { width: "100%", height: "38%" };

  return (
    <div data-testid="tv-root" className="bg-ink overflow-hidden"
      style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", padding: `${safe}vh ${safe}vw` }}>
      <div className="relative bg-cream overflow-hidden flex" data-testid="tv-layout"
        style={{ width: "100%", height: "100%", flexDirection: landscape ? "row" : "column" }}>
        <div className="relative flex flex-col overflow-hidden" style={{ ...callerSize, padding: "3vmin", flexShrink: 0 }} data-testid="tv-caller">
          <div className="flex items-center justify-between shrink-0">
            <div className="flex items-center min-w-0">
              {clinic?.logo_url ? (
                <img src={clinic.logo_url} alt="logo" style={{ height: "5.5vmin", width: "auto", objectFit: "contain", marginRight: "1.5vmin" }} />
              ) : (
                <BrandIcon style={{ width: "5.5vmin", height: "5.5vmin", marginRight: "1.5vmin" }} className="" testid="tv-brand-icon" />
              )}
              <span className="font-heading font-bold text-ink truncate" style={{ fontSize: "3.2vmin" }}>{clinic?.name}</span>
            </div>
            <div className="flex items-center text-cappuccino font-semibold shrink-0" style={{ fontSize: "3vmin" }}>
              <Clock style={{ width: "3vmin", height: "3vmin", marginRight: "1vmin" }} /> <Clockface />
            </div>
          </div>

          <div className="flex-1 flex flex-col items-center justify-center text-center" style={{ minHeight: 0 }}>
            <CallHero call={call} clinic={clinic} color={color} hasMedia={hasMedia} landscape={landscape} />
          </div>

          {activeCalls.length > 1 && (
            <div className="flex flex-wrap justify-center shrink-0" style={{ marginBottom: "2vmin" }} data-testid="tv-active-board">
              {activeCalls.map((p) => (
                <div key={p.id} className="flex items-center rounded-md bg-white border border-border" style={{ padding: "0.8vmin 1.6vmin", margin: "0.5vmin" }}>
                  <span className="font-heading font-semibold text-ink" style={{ fontSize: "2vmin", marginRight: "1vmin" }}>{firstLast(p.name)}</span>
                  <span className="font-bold text-white rounded-sm px-2" style={{ background: color, fontSize: "1.6vmin" }}>{p.room_name}</span>
                </div>
              ))}
            </div>
          )}

          <QueueLists upNext={state?.up_next || []} recent={state?.recent_calls || []} color={color} />

          {flash && <div className="pointer-events-none animate-pulse-ring" style={{ ...FILL, boxShadow: `inset 0 0 0 8px ${color}` }} />}
        </div>

        {hasMedia && (
          <div className="relative bg-black overflow-hidden" style={{ ...mediaSize, flexShrink: 0 }} data-testid="tv-media-side">
            <MediaPanel items={mediaItems} soundOn={soundOn} volume={mediaVol} skipSignal={skipSignal}
              onAutoplayBlocked={() => setSoundOn(false)} />
          </div>
        )}

        {!soundOn && <SoundOverlay onUnlock={unlock} />}
        <TvToolbar hasMedia={hasMedia} canSkip={mediaItems.length > 1} onSkip={skipMedia} volume={volume} setVolume={setVolume} safe={safe} setSafe={setSafe} voiceOk={speechSupported()} voiceError={voiceError} onTestVoice={testVoice} />
      </div>
    </div>
  );
}
